import React, { useState, useEffect } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { getAllMilestones } from "services/userServices";
import { Button, Grid } from "@mui/material";
import SnackBar from "components/SnackBar/SnackBar";
import { styled } from "@mui/material/styles";
import {
  getFrameworkMilestones,
  saveFrameworkMilestones,
} from "services/learnPointUserServices";
import BackdropComponent from "components/Spinner/Spinner";
import PropTypes from "prop-types";
import { useAuth } from "context/AuthContext";

const CheckBoxStyle = {
  "&.Mui-checked": {
    color: "#3299FF",
  },
};

const ColorButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#0ea5ab",
  "&:hover": {
    backgroundColor: "#259997",
  },
}));

const ColorOutlinedButton = styled(Button)(({ theme }) => ({
  color: "#0ea5ab",
  borderColor: "#259997",
}));

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function LearnpointDefaultMilestones(props) {
  const [checked, setChecked] = useState([]);
  const [existingFrameworkMilestones, setExistingFrameworkMilestones] =
    useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const frameworkId = props.frameworkId;
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const { hasPermission } = useAuth();
  const hasEditAccess = hasPermission("LD_Group_Admin");

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const prepareSavePayload = () => {
    const payload = {
      frameworkId: frameworkId,
      milestoneIds: checked.map((item) => ({ milestoneId: item })),
      createdBy: localStorage.getItem("User"),
    };
    return payload;
  };

  const handleSave = async () => {
    setLoading(true);
    const payload = prepareSavePayload();
    const response = await saveFrameworkMilestones(payload);
    if (response?.statusCode === 200) {
      await fetchDefaultMilestones();
      setSnack({
        open: true,
        message: `Saved successfully`,
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: `Save failed, some internal error occured.`,
        severity: "error",
      });
    }
    setSelectAllChecked(false);
    setLoading(false);
  };

  const handleCheckAll = () => {
    const selectAllStatus = !selectAllChecked;
    if (selectAllStatus && hasEditAccess) {
      const allMilestonesilestoneIds = milestones.map(
        (milestone) => milestone?.MilestoneId,
      );
      setChecked(allMilestonesilestoneIds);
    } else {
      setChecked([]);
    }
    setSelectAllChecked(selectAllStatus);
  };

  const handleReset = () => {
    setSelectAllChecked(false);
    setChecked(existingFrameworkMilestones);
  };

  const fetchDefaultMilestones = async () => {
    const frameworkMilestonesResponse =
      await getFrameworkMilestones(frameworkId);
    if (frameworkMilestonesResponse?.status === true) {
      const existingFrameworkMilestoneIds =
        frameworkMilestonesResponse?.data.map((item) => item?.MilestoneId);
      setChecked(existingFrameworkMilestoneIds);
      setExistingFrameworkMilestones(existingFrameworkMilestoneIds);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
        }

        const allMilstonesResponse = await getAllMilestones();

        if (cancelled) return;

        if (allMilstonesResponse?.status === true) {
          if (!cancelled) {
            setMilestones(allMilstonesResponse?.data);
          }
        } else {
          if (!cancelled) {
            setSnack({
              open: true,
              message: `Some internal error occured`,
              severity: "error",
            });
          }
        }

        await fetchDefaultMilestones();

        if (cancelled) return;

        if (!cancelled) {
          setLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: `Some internal error occured!`,
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid container>
        <Grid item xs={3}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleCheckAll}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={!hasEditAccess ? false : selectAllChecked}
                  disabled={!hasEditAccess}
                  tabIndex={-1}
                  disableRipple
                  sx={CheckBoxStyle}
                />
              </ListItemIcon>
              <ListItemText
                primary={"Select All"}
                primaryTypographyProps={{ fontWeight: "bold" }}
                sx={{ color: "#3299FF" }}
              />
            </ListItemButton>
          </ListItem>
        </Grid>
        <Grid item xs={7}></Grid>

        <Grid item xs={0.7}>
          <CustomButton
            variant="contained"
            onClick={handleSave}
            disableRipple
            disableElevation
            disabled={!hasEditAccess}
            sx={{
              color: !hasEditAccess ? "inherit" : "",
              cursor: !hasEditAccess ? "not-allowed" : "pointer",
              backgroundColor: !hasEditAccess ? "none" : "",
            }}
          >
            Save
          </CustomButton>
        </Grid>
        <Grid item xs={1}>
          <CustomOutlinedButton
            variant="outlined"
            size="small"
            onClick={handleReset}
            disableRipple
            disableElevation
            disabled={!hasEditAccess}
          >
            Reset
          </CustomOutlinedButton>
        </Grid>
        <Grid item xs={12}>
          <List>
            <Grid container>
              {milestones.map((value) => {
                const labelId = `checkbox-list-label-${value?.MilestoneId}`;

                return (
                  <Grid key={value?.MilestoneId} item xs={2}>
                    <ListItem key={value?.MilestoneId} disablePadding>
                      <ListItemButton
                        onClick={handleToggle(value?.MilestoneId)}
                        dense
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={
                              checked.indexOf(value?.MilestoneId) !== -1 &&
                              hasEditAccess
                            }
                            disabled={!hasEditAccess}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ "aria-labelledby": labelId }}
                            sx={CheckBoxStyle}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={labelId}
                          primary={value?.Milestone}
                          secondary={value?.MilestoneType}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                );
              })}
            </Grid>
          </List>
        </Grid>
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
}

LearnpointDefaultMilestones.propTypes = {
  frameworkId: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool,
};
